import { useState } from 'react';
import type { FormatMeta } from '@pgp/shared';
import { net, useApp } from '../net';
import { useTheme } from '../ui/theme';
import { Admin } from './Admin';

export function Lobby() {
  const app = useApp();
  const [format, setFormat] = useState<string>('gen9randombattle');
  const [theme, toggleTheme] = useTheme();

  const me = app.user;
  const others = app.users.filter((u) => u.id !== me?.id);
  const online = others.filter((u) => u.online);
  const offline = others.filter((u) => !u.online);

  const incoming = app.challenges.filter((c) => c.to === me?.id);
  const outgoing = app.challenges.filter((c) => c.from === me?.id);

  // Formats with their own label are not tied to a generation, so they get
  // their own group rather than being filed under one.
  const special = app.formats.filter((f) => f.label);
  const byGen = new Map<number, FormatMeta[]>();
  for (const f of app.formats) {
    if (f.label) continue;
    const list = byGen.get(f.gen) ?? [];
    list.push(f);
    byGen.set(f.gen, list);
  }

  return (
    <div className="lobby">
      <header className="lobby-top">
        <div>
          <h1 className="lobby-title">
            <span className="ball-mark" aria-hidden="true" />
            Family Games
          </h1>
          <span className={`conn ${app.connection}`}>
            {app.connection === 'online' ? 'connected' : app.connection}
          </span>
        </div>
        <div className="who">
          <button
            className="link"
            onClick={toggleTheme}
            title={theme === 'light' ? 'Dark mode' : 'Light mode'}
            aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          >
            {theme === 'light' ? '☾' : '☀'}
          </button>
          <span>{me?.name}</span>
          <button className="link" onClick={() => net.logout()}>Sign out</button>
        </div>
      </header>

      {app.error && <div className="banner error">{app.error}</div>}

      <section>
        <h2>Format</h2>
        <select value={format} onChange={(e) => setFormat(e.target.value)}>
          {special.length > 0 && (
            <optgroup label="Special">
              {special.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </optgroup>
          )}
          {[...byGen.entries()]
            .sort((a, b) => b[0] - a[0])
            .map(([gen, formats]) => (
              <optgroup key={gen} label={`Generation ${gen}`}>
                {formats.map((f) => (
                  <option key={f.id} value={f.id}>
                    Gen {f.gen} {f.name}
                  </option>
                ))}
              </optgroup>
            ))}
        </select>
        <p className="hint">
          Teams are generated for you. Every generation plays differently — Gen 1 has no
          abilities or held items, Gen 9 has Terastallization.
        </p>
      </section>

      {incoming.length > 0 && (
        <section>
          <h2>Challenges for you</h2>
          {incoming.map((c) => (
            <div key={c.from} className="challenge">
              <span><b>{c.fromName}</b> wants to play {c.formatName}</span>
              <div>
                <button
                  className="primary"
                  onClick={() => net.send({ t: 'lobby.accept', from: c.from })}
                >
                  Accept
                </button>
                <button
                  className="link"
                  onClick={() => net.send({ t: 'lobby.decline', from: c.from })}
                >
                  Decline
                </button>
              </div>
            </div>
          ))}
        </section>
      )}

      <section>
        <h2>Players</h2>
        {others.length === 0 && (
          <p className="hint">
            {me?.isAdmin
              ? 'Nobody else has an account yet. Create an invite link below.'
              : 'Nobody else has an account yet.'}
          </p>
        )}
        <ul className="players">
          {[...online, ...offline].map((u) => {
            const pending = outgoing.find((c) => c.to === u.id);
            return (
              <li key={u.id} className={u.online ? '' : 'is-offline'}>
                <span className={`dot ${u.online ? 'on' : ''}`} />
                <span className="pname">{u.name}</span>
                {pending ? (
                  <button className="link" onClick={() => net.send({ t: 'lobby.cancel', to: u.id })}>
                    Cancel challenge
                  </button>
                ) : (
                  <button
                    disabled={!u.online}
                    onClick={() =>
                      net.send({ t: 'lobby.challenge', to: u.id, game: 'pokemon', format })
                    }
                  >
                    Challenge
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      {me?.isAdmin && <Admin />}

      {app.rooms.length > 0 && (
        <section>
          <h2>Battles</h2>
          <ul className="rooms">
            {app.rooms.map((r) => (
              <li key={r.id}>
                <span>
                  {r.players.map((p) => p.name).join(' vs ')} — {r.formatName}
                  {r.ended ? ' (finished)' : ''}
                </span>
                <button onClick={() => {
                  if (app.joined[r.id]) net.openRoom(r.id);
                  else net.send({ t: 'room.join', roomId: r.id });
                }}>
                  {r.players.some((p) => p.userId === me?.id) ? 'Return' : 'Watch'}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
